/**
 * Section navigation rail for a long form (TRUS-571).
 *
 * IFR's v2 ECM assessment is 18 sections and 260 fields. Collapsing the sections
 * made it usable; it did not make it navigable — reaching section 14 still means
 * scrolling past thirteen headers. The design puts a sticky 230px rail beside the
 * form, listing each section with its field count.
 *
 * OPT-IN, PER TEMPLATE. A rail is right for a 260-field instrument and wrong for
 * a 6-field intake form, so it renders only when the template asks for it via
 * `settings.navigation`. That gives per-FORM and per-ORG scoping in one move with
 * no new mechanism: a form_templates row already carries cbo_id, so a setting on
 * the template is already scoped to both.
 *
 * IT LIVES ON THE PAGE, NOT IN FormRenderer. The rail is page chrome. Putting it
 * inside the renderer every org shares is how a layout decision for one
 * instrument becomes everyone's problem — the same mistake that put `divider` in
 * one renderer and `multiselect` in the other.
 */

import React from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';

export const SECTION_ANCHOR_PREFIX = 'form-section-';

export function sectionAnchorId(sectionId: string): string {
  return `${SECTION_ANCHOR_PREFIX}${sectionId}`;
}

/** Does this template want a rail? Absent or anything else means no. */
export function wantsSectionNav(settings: any): boolean {
  return (settings?.navigation?.style ?? null) === 'rail';
}

interface FormSectionNavProps {
  sections: Array<{ section_id?: string; title?: string; fields?: any[]; order?: number }>;
  /** Section ids to leave out — e.g. one already shown as a header panel. */
  omit?: string[];
}

export const FormSectionNav: React.FC<FormSectionNavProps> = ({ sections, omit }) => {
  const { t } = useAppTranslation();
  const [active, setActive] = React.useState<string | null>(null);
  const skip = React.useMemo(() => new Set(omit || []), [omit]);

  const items = React.useMemo(
    () =>
      [...(sections || [])]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .filter((s) => s.section_id && !skip.has(s.section_id)),
    [sections, skip],
  );

  // Highlight whichever section is nearest the top of the viewport. An
  // IntersectionObserver rather than a scroll listener: a 260-field page fires
  // scroll events continuously, and recomputing positions on each one is how a
  // long form starts to feel heavy.
  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible?.target?.id) {
          setActive(visible.target.id.replace(SECTION_ANCHOR_PREFIX, ''));
        }
      },
      { rootMargin: '-64px 0px -70% 0px' },
    );
    items.forEach((s) => {
      const el = document.getElementById(sectionAnchorId(s.section_id as string));
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [items]);

  if (items.length < 2) return null;

  const go = (sectionId: string) => {
    const el = document.getElementById(sectionAnchorId(sectionId));
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Move focus as well as the viewport, or a keyboard user jumps the page and
    // then tabs from wherever they were — which is worse than not jumping.
    el.setAttribute('tabindex', '-1');
    (el as HTMLElement).focus({ preventScroll: true });
  };

  return (
    <nav
      aria-label={t('forms.sectionNav', 'Form sections')}
      className="sticky top-4 max-h-[80vh] overflow-auto rounded-lg p-2 text-sm"
      style={{
        background: 'var(--wf-surface, #FFFFFF)',
        border: '1px solid var(--wf-border, #E5E7EB)',
      }}
    >
      <ul className="space-y-0.5">
        {items.map((s) => {
          const id = s.section_id as string;
          const isActive = active === id;
          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => go(id)}
                aria-current={isActive ? 'true' : undefined}
                className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left hover:opacity-80"
                style={{
                  background: isActive ? 'var(--wf-accent-soft, #EFF6FF)' : 'transparent',
                  color: isActive ? 'var(--wf-accent, #2563EB)' : 'var(--wf-text, #111827)',
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                <span className="truncate">{s.title}</span>
                <span
                  className="shrink-0 text-xs"
                  style={{ color: 'var(--wf-muted, #6B7280)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {(s.fields || []).length}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default FormSectionNav;
